import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Outlet, NavLink, Link, Navigate } from 'react-router-dom';
import { auth } from './services/api';
import Login from './components/Login';
import TableauDeBord from './components/TableauDeBord';
import Personnes from './components/Personnes';
import Campagnes from './components/Campagnes';
import Mentions from './components/Mentions';
import Journal from './components/Journal';
import Configuration from './components/Configuration';
import Formulaire from './public/Formulaire';
import Retrait from './public/Retrait';
import Preuve from './public/Preuve';
import { IcGalerie, IcRecu, IcEclair, IcFichier, IcLivre, IcReglages, IcSortie } from './components/Icones';
import './App.css';

// Gabarit de la console : en-tête A97 + navigation latérale (bureau) /
// barre d'onglets basse (mobile) — même coquille que NDF.
function Layout({ user, onLogout }) {
  const admin = user.role === 'admin';
  const dpo = user.role === 'dpo' || admin;
  const onglets = [
    { to: '/', label: 'Tableau de bord', icone: <IcGalerie />, fin: true },
    { to: '/personnes', label: 'Personnes', icone: <IcRecu /> },
    { to: '/campagnes', label: 'Campagnes', icone: <IcEclair /> },
    { to: '/mentions', label: 'Mentions', icone: <IcFichier /> },
    ...(dpo ? [{ to: '/journal', label: 'Journal', icone: <IcLivre /> }] : []),
    ...(admin ? [{ to: '/configuration', label: 'Configuration', icone: <IcReglages /> }] : []),
  ];
  const rendreOnglet = (o) => (
    <NavLink key={o.to} to={o.to} end={o.fin} className={({ isActive }) => `rg-nav-item ${isActive ? 'active' : ''}`}>
      <span className="rg-nav-icone" aria-hidden="true">{o.icone}</span>
      <span className="rg-nav-label">{o.label}</span>
    </NavLink>
  );
  return (
    <div className="rg-shell">
      <header className="rg-header">
        <Link to="/" className="rg-brand">A<span className="rg-gold">97</span><span className="rg-brand-app">RGPD</span></Link>
        <div className="rg-header-user">
          <span className="rg-header-nom">{user.prenom ? `${user.prenom} ${user.nom || ''}`.trim() : user.email}</span>
          <span className="rg-role">{user.role}</span>
          <button className="rg-logout" onClick={onLogout} aria-label="Se déconnecter" title="Se déconnecter"><IcSortie taille={19} /></button>
        </div>
      </header>
      <div className="rg-layout">
        <nav className="rg-sidebar" aria-label="Navigation">{onglets.map(rendreOnglet)}</nav>
        <main className="rg-main"><Outlet /></main>
      </div>
      <nav className="rg-tabbar" aria-label="Navigation mobile">{onglets.map(rendreOnglet)}</nav>
    </div>
  );
}

// Pages publiques (formulaire, retrait, preuve) : aucune session requise.
function estPublique() {
  return /^\/(c|r|p)\/[a-z0-9]+$/i.test(window.location.pathname);
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const oidcHandled = useRef(false);

  useEffect(() => {
    if (estPublique()) { setLoading(false); return; }
    const params = new URLSearchParams(window.location.search);
    if ((process.env.NODE_ENV === 'development' || process.env.REACT_APP_ACCES_TEMPORAIRE === '1') && params.get('jeton')) {
      localStorage.setItem('rgpd-token', params.get('jeton'));
      window.location.replace('/');
      return;
    }
    const retourOidc = window.location.pathname === '/oidc/callback' || params.has('code');
    if (retourOidc && !oidcHandled.current) {
      oidcHandled.current = true;
      traiterRetourOidc(params);
    } else {
      verifierSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifierSession = async () => {
    if (localStorage.getItem('rgpd-token')) {
      try {
        const res = await auth.getMe();
        setUser(res.data.user);
      } catch (e) {
        localStorage.removeItem('rgpd-token');
      }
    }
    setLoading(false);
  };

  const traiterRetourOidc = async (params) => {
    const nettoyerUrl = () => window.history.replaceState({}, '', '/');
    try {
      const erreurOauth = params.get('error_description') || params.get('error');
      if (erreurOauth) throw new Error(erreurOauth);
      const code = params.get('code');
      const state = params.get('state');
      if (!code || !state) throw new Error('Réponse Microsoft incomplète');
      const res = await auth.microsoftCallback(code, state);
      nettoyerUrl();
      localStorage.setItem('rgpd-token', res.data.token);
      setUser(res.data.user);
    } catch (error) {
      nettoyerUrl();
      setAuthError(error.response?.data?.error || error.message || 'Échec de la connexion Microsoft');
    } finally {
      setLoading(false);
    }
  };

  const seDeconnecter = () => { localStorage.removeItem('rgpd-token'); setUser(null); };

  if (loading) return null;

  if (estPublique()) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/c/:token" element={<Formulaire />} />
          <Route path="/r/:token" element={<Retrait />} />
          <Route path="/p/:token" element={<Preuve />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (!user) return <Login authError={authError} />;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout user={user} onLogout={seDeconnecter} />}>
          <Route index element={<TableauDeBord user={user} />} />
          <Route path="personnes" element={<Personnes user={user} />} />
          <Route path="personnes/:id" element={<Personnes user={user} />} />
          <Route path="campagnes" element={<Campagnes user={user} />} />
          <Route path="campagnes/:id" element={<Campagnes user={user} />} />
          <Route path="mentions" element={<Mentions user={user} />} />
          <Route path="journal" element={<Journal user={user} />} />
          <Route path="configuration" element={user.role === 'admin' ? <Configuration user={user} /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
